import React from "react";

import styles from "./style.module.css";
import numeral from "numeral";

import { AutoSizer } from "react-virtualized";
import { BarChart, ResponsiveContainer, Line, LineChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

const DATA_EVAL_COLORS = {
  NOT_COMPLIED: "#f8cd7e",

  COMPLIED: "#54C06C"
};

const convertToPercentage = (num, sigFigs = 2) => {
  let formatString = "0.[0";

  for (let i = 1; i < sigFigs; i++) {
    formatString += "0";
  }

  formatString += "]";

  return numeral(num * 100).format(formatString) + "%";
};

const getLatestCompliance = (compliancePlot) => {
  const monitoringCount = compliancePlot ? compliancePlot.length : 0;

  const latestCompliance = {
    actComplianceRate: 0,
    notActComplianceRate: 0,
    actComplianceRateChange: 0,
    notActComplianceRateChange: 0,
    label: "",
    date: undefined
  };

  if (monitoringCount) {
    let latestIndex, previousIndex;
    for (let i = monitoringCount - 1; i >= 0 && (latestIndex === undefined || previousIndex === undefined); i--) {
      const currData = compliancePlot[i];
      if (currData && currData.complied !== undefined && currData.notComplied !== undefined) {
        if (latestIndex === undefined) {
          latestIndex = i;
        } else if (previousIndex === undefined) {
          previousIndex = i;
        }
      }
    }

    const lData = latestIndex !== undefined ? compliancePlot[latestIndex] : undefined;
    const pData = previousIndex !== undefined ? compliancePlot[previousIndex] : undefined;
    if (!latestCompliance.label) {
      latestCompliance.label = lData.label;
    }
    if (!latestCompliance.date) {
      latestCompliance.date = lData.date;
    }
    latestCompliance.actComplianceRate = lData && lData.actComplianceRate;
    latestCompliance.notActComplianceRate = lData && lData.notActComplianceRate;
    latestCompliance.actComplianceRateChange =
      lData && pData ? lData.actComplianceRate - pData.actComplianceRate : undefined;
    latestCompliance.notActComplianceRateChange =
      lData && pData ? lData.notActComplianceRate - pData.notActComplianceRate : undefined;
  }

  return latestCompliance;
};

const CustomToolTip = (props) => {
  const { active, payload, setupLabels, isMini, tooltipFollowKey, tooltipNotFollowKey, dataKey } = props;

  const currPayload = payload && payload[0] && payload[0].payload;

  if (!active || !currPayload) {
    return null;
  }

  if (isMini) {
    return (
      <div className={styles.cTooltip + " " + styles.isMini} style={{ fontSize: "14px" }}>
        <div className={styles.cHeader}>{currPayload.date}</div>
        {/* <div className={styles.cData}>
          <div className={styles.cLabel}>{"Followed Prediction:"}</div>
          <div className={styles.cValue}>{currPayload[tooltipFollowKey]}</div>
        </div>
        <div className={styles.cData}>
          <div className={styles.cLabel}>{"Ignored Prediction:"}</div>
          <div className={styles.cValue}>{currPayload[tooltipNotFollowKey]}</div>
        </div> */}
        <div className={styles.cData}>
          <div className={styles.cLabel}>{"Compliance:"}</div>
          <div className={styles.cValue}>{convertToPercentage(currPayload[dataKey])}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.cTooltip} style={{ fontSize: "14px" }}>
      <div className={styles.cHeader}>{currPayload.date}</div>
      <table className={styles.tTable}>
        <tbody>
          <tr>
            <td className={styles.tLabel}> </td>
            <td className={styles.tHeader}>{"Followed Recommendation"}</td>
            <td className={styles.tHeader}>{"Ignored Recommendation"}</td>
            <td className={styles.tHeader}>{"Total"}</td>
            <td className={styles.tHeader}>{"Compliance Rate"}</td>
          </tr>
          <tr>
            <td className={styles.tLabel}>
              <div className={styles.tLegendIndicator} style={{ background: DATA_EVAL_COLORS["COMPLIED"] }} />
              <span>{"Predict " + setupLabels.trueAction}</span>
            </td>
            <td>{currPayload.compliedAct}</td>
            <td>{currPayload.notCompliedNotAct}</td>
            <td className={styles.tCenter}>
              <span>{currPayload.compliedAct + currPayload.notCompliedNotAct}</span>
            </td>
            <td className={styles.tCenter}>
              <span>{convertToPercentage(currPayload.actComplianceRate)}</span>
            </td>
          </tr>
          <tr>
            <td className={styles.tLabel}>
              <div className={styles.tLegendIndicator} style={{ background: DATA_EVAL_COLORS["NOT_COMPLIED"] }} />
              <span>{"Predict " + setupLabels.falseAction}</span>
            </td>
            <td>{currPayload.compliedNotAct}</td>
            <td>{currPayload.notCompliedAct}</td>
            <td className={styles.tCenter}>
              <span>{currPayload.compliedNotAct + currPayload.notCompliedAct}</span>
            </td>
            <td className={styles.tCenter}>
              <span>{convertToPercentage(currPayload.notActComplianceRate)}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default class NextComplianceGraph extends React.PureComponent {
  render() {
    const { compliancePlot, setupLabels, isMini } = this.props;

    if (!compliancePlot) {
      return (
        <div className={styles.userFeedbackWrapper}>
          <div className={styles.nextUserFeedbackTitle} style={{ paddingLeft: "15px" }}>
            {"User Analytics"}
          </div>
          <div style={{ padding: "15px 15px", fontWeight: "bold" }}>User Analytics Not Configured</div>
        </div>
      );
    }

    if (isMini) {
      return <MiniComplianceGraph {...this.props} />;
    }

    const latestCompliance = getLatestCompliance(compliancePlot);

    return (
      <div className={styles.userFeedbackWrapper}>
        <div className={styles.nextUserFeedbackTitle} style={{ paddingLeft: "15px" }}>
          {"User Analytics"}
        </div>
        <div className={styles.userFeedbackLegend + " " + styles.dataEvaluationLegendWrapper}>
          <LegendItem
            color={DATA_EVAL_COLORS["COMPLIED"]}
            label={setupLabels.trueAction}
            value={latestCompliance.actComplianceRate}
            valueDiff={latestCompliance.actComplianceRateChange}
            // REMOVE THIS LINE IF WE WANT TO ONLY SHOW ONE DATE
            date={latestCompliance.date}
          />
          <LegendItem
            color={DATA_EVAL_COLORS["NOT_COMPLIED"]}
            label={setupLabels.falseAction}
            value={latestCompliance.notActComplianceRate}
            valueDiff={latestCompliance.notActComplianceRateChange}
            date={latestCompliance.date}
          />
        </div>
        <div className={styles.dataEvaluationBody}>
          <ResponsiveContainer height={400}>
            <LineChart
              width={500}
              height={300}
              data={compliancePlot}
              margin={{
                top: 0,
                right: 30,
                left: 20,
                bottom: 5
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis tickFormatter={(tick) => convertToPercentage(tick)} domain={[0, 1]} />
              <Tooltip content={<CustomToolTip setupLabels={setupLabels} />} />
              <Line dataKey="actComplianceRate" stroke={DATA_EVAL_COLORS["COMPLIED"]} strokeWidth={2} dot={true} />
              <Line
                dataKey="actComplianceRate"
                stroke={DATA_EVAL_COLORS["COMPLIED"]}
                strokeWidth={2}
                dot={false}
                strokeDasharray="5 5"
                connectNulls
              />
              <Line
                dataKey="notActComplianceRate"
                stroke={DATA_EVAL_COLORS["NOT_COMPLIED"]}
                strokeWidth={2}
                dot={true}
              />
              <Line
                dataKey="notActComplianceRate"
                stroke={DATA_EVAL_COLORS["NOT_COMPLIED"]}
                strokeWidth={2}
                dot={false}
                strokeDasharray="5 5"
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }
}

class LegendItem extends React.PureComponent {
  renderValue() {
    const { color, label, value, valueDiff, isMini, date } = this.props;
    return (
      <React.Fragment>
        <div className={styles.userComplianceLegendTitle + " " + (isMini ? styles.isMini : "")}>
          {convertToPercentage(value)}
        </div>
        {valueDiff !== undefined ? (
          <div className={styles.userComplianceLegendSubTitle + " " + (isMini ? styles.isMini : "")}>
            {(valueDiff >= 0 ? "+" : "") + convertToPercentage(valueDiff, isMini ? 1 : 2)}
          </div>
        ) : undefined}
      </React.Fragment>
    );
  }

  render() {
    const { color, label, value, valueDiff, isMini, date } = this.props;
    if (isMini) {
      return <div className={styles.miniComplianceLegend}>{this.renderValue()}</div>;
    }
    return (
      <div className={styles.userComplianceLegendWrapper}>
        <div className={styles.userComplianceLegend} style={{ borderBottomColor: color }}>
          <div className={styles.userComplianceLegendItem}>
            <div className={styles.userComplianceLegendLabel}>{label}</div>
            <div className={styles.userComplianceLegendSublabel}>{"Recommendations Followed"}</div>
          </div>
          {this.renderValue()}
        </div>
        {date && !isMini ? <div className={styles.infoItemSubtitle}>{date}</div> : <React.Fragment />}
      </div>
    );
  }
}

class MiniComplianceGraph extends React.PureComponent {
  render() {
    const { compliancePlot, setupLabels } = this.props;

    const latestCompliance = getLatestCompliance(compliancePlot);

    return (
      <div className={styles.miniComplianceWrapper}>
        <MiniComplianceSparkGraph
          color={DATA_EVAL_COLORS["COMPLIED"]}
          label={setupLabels.trueAction}
          value={latestCompliance.actComplianceRate}
          valueDiff={latestCompliance.actComplianceRateChange}
          data={compliancePlot}
          dataKey={"actComplianceRate"}
          tooltipFollowKey={"compliedAct"}
          tooltipNotFollowKey={"notCompliedAct"}
        />
        <MiniComplianceSparkGraph
          color={DATA_EVAL_COLORS["NOT_COMPLIED"]}
          label={setupLabels.falseAction}
          value={latestCompliance.notActComplianceRate}
          valueDiff={latestCompliance.notActComplianceRateChange}
          data={compliancePlot}
          dataKey={"notActComplianceRate"}
          tooltipFollowKey={"compliedNotAct"}
          tooltipNotFollowKey={"notCompliedNotAct"}
        />
      </div>
    );
  }
}

class MiniComplianceSparkGraph extends React.PureComponent {
  render() {
    const { color, label, value, valueDiff, data, dataKey, tooltipFollowKey, tooltipNotFollowKey } = this.props;

    return (
      <div className={styles.miniComplianceGraphWrapper}>
        <LegendItem color={color} label={label} value={value} valueDiff={valueDiff} isMini />
        <div className={styles.miniComplianceGraph}>
          <AutoSizer>
            {({ width, height }) => (
              <LineChart
                width={width}
                height={height}
                data={data}
                margin={{
                  top: 5,
                  right: 0,
                  left: 0,
                  bottom: 5
                }}
              >
                <XAxis dataKey="date" hide />
                <YAxis domain={[0, 1]} hide />
                <Tooltip
                  content={
                    <CustomToolTip
                      dataKey={dataKey}
                      tooltipFollowKey={tooltipFollowKey}
                      tooltipNotFollowKey={tooltipNotFollowKey}
                      isMini
                    />
                  }
                />
                <Line dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} />
                <Line dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} strokeDasharray="5 5" connectNulls />
              </LineChart>
            )}
          </AutoSizer>
        </div>
      </div>
    );
  }
}
